import { FC, MutableRefObject, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Stack } from '@bonusly/design-system';

import { Bonus } from '../../../../../types/bonus';
import { useSayThanks } from '../../hooks/use-say-thanks';
import { CommentComposer } from './comment-composer';
import { CommentsContainer } from './comments-container';

export interface CommentsProps {
	bonus: Bonus;
	feedFilter?: string;
	initalText?: string;
	isModalDisplay?: boolean;
	/** True when the whole composer is rendered inside the post modal (drives the Bizy bubble left so it doesn't overflow). */
	isInModal?: boolean;
	isOpen: boolean;
	onClose?: VoidFunction;
	onCommentSubmitFailure: (reason: string) => void;
	onOpen?: VoidFunction;
	commentBoxRef?: MutableRefObject<HTMLDivElement | null>;
}

export const Comments: FC<CommentsProps> = ({
	bonus,
	feedFilter,
	isOpen,
	onClose,
	onOpen,
	isModalDisplay = false,
	isInModal = false,
	commentBoxRef,
	...rest
}) => {
	const { t } = useTranslation('recognition');

	const commentComposerId = `comment-composer-${bonus.id}`;

	useEffect(() => {
		if (isOpen) {
			// NOTE: Use setTimeout to wait for the CommentComposer to fully render and calculate its height
			const timeoutId = setTimeout(() => {
				const element = document.getElementById(commentComposerId);
				if (element) {
					const elementRect = element.getBoundingClientRect();
					const bottomOffset = elementRect.bottom - window.innerHeight;

					// Only scroll if the bottom of the element is below the viewport
					if (bottomOffset > 0) {
						window.scrollBy({ top: bottomOffset + 16, behavior: 'smooth' });
					}
				}
			}, 100);

			return () => clearTimeout(timeoutId);
		}
	}, [isOpen, commentComposerId]);

	const hasComments = !!bonus.child_bonuses?.length;
	const hasFeedback = !!bonus.evaluations?.length;
	const displayComments = hasComments || hasFeedback;
	const sayThanks = useSayThanks(bonus);

	return (
		<Stack spacing={2}>
			{displayComments && (
				<CommentsContainer
					bonus={bonus}
					feedFilter={feedFilter}
					isModalDisplay={isModalDisplay}
					data-testid="comments-container"
					parentBonus={bonus}
					bg="bg.subtle"
				/>
			)}

			{hasComments && !isOpen && (
				<Button onClick={onOpen} backgroundColor="bg.subtle" colorScheme="secondary" variant="alpha">
					{sayThanks ? t('bonus_card.thanks_button') : t('comment.add_comment_button')}
				</Button>
			)}

			{isOpen && <CommentComposer id={commentComposerId} bonus={bonus} onSuccess={onClose} commentBoxRef={commentBoxRef} isInModal={isInModal} {...rest} />}
		</Stack>
	);
};
